import { User } from "~lib/commonType/auth";
import { CoverLetter } from "~lib/commonType/coverLetter";
import { Resume } from "~lib/commonType/resume";
import {
  COVER_LETTER_IMAGES,
  DISAPPEAR_RED_DOT_IN_NAV_BAR,
  DISAPPEAR_AI_TAG_IN_MEMU,
  IS_USER,
  RANDOM_FIRST_NAMES,
  RANDOM_LAST_NAMES,
  RESUME_IMAGES,
  RESUME_USER,
  TEMPLATE,
  DISAPPEAR_AI_TAG_IN_MEMU_ON_WEB,
  DISAPPEAR_AI_TAG_IN_SUDE_BAR,
  RESUME_COLOR_RECORD
} from "~lib/keyNamesOfLocalStorage";

export const hasLocalStorage = (): boolean => {
  return typeof Storage !== "undefined" && typeof localStorage === "object";
};

export const getLocalUserObject = (): User | Record<string, never> => {
  if (hasLocalStorage()) {
    const tempUserObj = window.localStorage.getItem(RESUME_USER);
    if (tempUserObj !== "undefined" && tempUserObj) {
      return JSON.parse(tempUserObj);
    }
  }
  return {};
};

export const setLocalUserObject = (
  user: User | Record<string, never>
): void => {
  if (hasLocalStorage() && user) {
    localStorage.setItem(RESUME_USER, JSON.stringify(user));
  }
};

export const removeLocalStorageUser = (): void => {
  if (hasLocalStorage()) {
    localStorage.removeItem(RESUME_USER);
  }
};

export const getLocalStorageIsUser = (): string => {
  let isUser = "false";
  if (hasLocalStorage()) {
    isUser = localStorage.getItem(IS_USER);
  }
  return isUser;
};

export const setLocalStorageIsUser = (): void => {
  if (hasLocalStorage()) {
    localStorage.setItem(IS_USER, "true");
  }
};

export const removeLocalStorageIsUser = (): void => {
  if (hasLocalStorage()) {
    localStorage.removeItem(IS_USER);
  }
};

export const getLocalResumeOrCoverLetterObject = (isCoverLetter = false) => {
  const inLocalStorageResumeOrCoverLetter = isCoverLetter
    ? localStorage.getItem(COVER_LETTER_IMAGES)
    : localStorage.getItem(RESUME_IMAGES);

  return JSON.parse(inLocalStorageResumeOrCoverLetter);
};

export const setLocalResumeImages = (resume: Resume, resumeImg: string) => {
  const items = localStorage.getItem(RESUME_IMAGES) || {};
  let parsedItems = {};
  if (typeof items === "string") {
    parsedItems = JSON.parse(items);
  }
  parsedItems[resume.id] = {
    resumeOrCoverLetter: resume,
    resumeOrCoverLetterImg: resumeImg
  };
  localStorage.setItem(RESUME_IMAGES, JSON.stringify(parsedItems));
};

export const setLocalCoverLetterImages = (
  coverLetter: CoverLetter,
  coverLetterImg: string
) => {
  const items = localStorage.getItem(COVER_LETTER_IMAGES) || {};
  let parsedItems = {};
  if (typeof items === "string") {
    parsedItems = JSON.parse(items);
  }
  parsedItems[coverLetter.id] = {
    resumeOrCoverLetter: coverLetter,
    resumeOrCoverLetterImg: coverLetterImg
  };
  localStorage.setItem(COVER_LETTER_IMAGES, JSON.stringify(parsedItems));
};

export const removeLocalStorageResumeOrCoverLetter = (
  id: string,
  isResume: boolean
): void => {
  const items = isResume
    ? localStorage.getItem(RESUME_IMAGES)
    : localStorage.getItem(COVER_LETTER_IMAGES);
  const parsedItems = JSON.parse(items);

  if (parsedItems && Object.prototype.hasOwnProperty.call(parsedItems, id)) {
    delete parsedItems[id];
    isResume
      ? localStorage.setItem(RESUME_IMAGES, JSON.stringify(parsedItems))
      : localStorage.setItem(COVER_LETTER_IMAGES, JSON.stringify(parsedItems));
  }
};

export const getLocalStorageTemplate = (): string => {
  let template = "rec1";
  if (hasLocalStorage()) {
    template = localStorage.getItem(TEMPLATE);
  }
  return template;
};

export const setLocalStorageTemplate = (templateCode: string): void => {
  if (hasLocalStorage()) {
    localStorage.setItem(TEMPLATE, templateCode);
  }
};

export const removeLocalStorageTemplate = (): void => {
  if (hasLocalStorage()) {
    localStorage.removeItem(TEMPLATE);
  }
};

export const getLocalStorageFirstNamePlaceholder = (): string => {
  let firstNamePlaceholder: string;
  if (hasLocalStorage()) {
    firstNamePlaceholder = localStorage.getItem(RANDOM_FIRST_NAMES);
  }
  return firstNamePlaceholder;
};

export const setLocalStorageFirstNamePlaceholder = (
  randomFirstNamePlaceholder: string
): void => {
  if (hasLocalStorage() && !getLocalStorageFirstNamePlaceholder()) {
    localStorage.setItem(RANDOM_FIRST_NAMES, randomFirstNamePlaceholder);
  }
};

export const getLocalStorageLastNamePlaceholder = (): string => {
  let lastNamePlaceholder: string;
  if (hasLocalStorage()) {
    lastNamePlaceholder = localStorage.getItem(RANDOM_LAST_NAMES);
  }
  return lastNamePlaceholder;
};

export const setLocalStorageLastNamePlaceholder = (
  randomLastNamePlaceholder: string
): void => {
  if (hasLocalStorage() && !getLocalStorageLastNamePlaceholder()) {
    localStorage.setItem(RANDOM_LAST_NAMES, randomLastNamePlaceholder);
  }
};

export const removeLocalStorageNamePlaceholder = (): void => {
  if (hasLocalStorage()) {
    localStorage.removeItem(RANDOM_FIRST_NAMES);
    localStorage.removeItem(RANDOM_LAST_NAMES);
  }
};

export const getLocalStorageRedDotDisplaysInNavBar = (): string => {
  let disappearDot = "false";
  if (hasLocalStorage()) {
    disappearDot = localStorage.getItem(DISAPPEAR_RED_DOT_IN_NAV_BAR);
  }
  return disappearDot;
};

export const setLocalStorageRedDotDisplaysInNavBar = () => {
  if (hasLocalStorage()) {
    localStorage.setItem(DISAPPEAR_RED_DOT_IN_NAV_BAR, "true");
  }
};

export const getLocalStorageAITagInMenu = (): string => {
  let disappearAITag = "false";
  if (hasLocalStorage()) {
    disappearAITag = localStorage.getItem(DISAPPEAR_AI_TAG_IN_MEMU);
  }
  return disappearAITag;
};

export const setLocalStorageAITagInMenu = () => {
  if (hasLocalStorage()) {
    localStorage.setItem(DISAPPEAR_AI_TAG_IN_MEMU, "true");
  }
};

export const getLocalStorageAITagInMenuOnWeb = (): string => {
  let disappearAITag = "false";
  if (hasLocalStorage()) {
    disappearAITag = localStorage.getItem(DISAPPEAR_AI_TAG_IN_MEMU_ON_WEB);
  }
  return disappearAITag;
};

export const setLocalStorageAITagInMenuOnWeb = () => {
  if (hasLocalStorage()) {
    localStorage.setItem(DISAPPEAR_AI_TAG_IN_MEMU_ON_WEB, "true");
  }
};

export const getLocalStorageAITagInSideBar = (): string => {
  let disappearAITag = "false";
  if (hasLocalStorage()) {
    disappearAITag = localStorage.getItem(DISAPPEAR_AI_TAG_IN_SUDE_BAR);
  }
  return disappearAITag;
};

export const setLocalStorageAITagInSideBar = () => {
  if (hasLocalStorage()) {
    localStorage.setItem(DISAPPEAR_AI_TAG_IN_SUDE_BAR, "true");
  }
};

export const getLocalResumeColorRecord = (): string => {
  return JSON.parse(localStorage.getItem(RESUME_COLOR_RECORD)) || {};
};
